import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Box,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import taxClassificationAPI, {
  TaxClassification,
} from "../../services/tax-classification-api";

interface FormData {
  displayText: string;
  ledgerText: string;
  taxPercent: string;
}

interface TaxClassificationCardProps {
  hideHeader?: boolean;
  shouldLoad?: boolean;
}

export interface TaxClassificationCardRef {
  openAddDialog: () => void;
}

const TaxClassificationCard = forwardRef<
  TaxClassificationCardRef,
  TaxClassificationCardProps
>(({ hideHeader = false, shouldLoad = false }, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [classifications, setClassifications] = useState<TaxClassification[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>({
    displayText: "",
    ledgerText: "",
    taxPercent: "",
  });
  useEffect(() => {
    if (shouldLoad) {
      loadClassifications();
    }
  }, [shouldLoad]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openAddDialog: () => handleOpenDialog(),
  }));

  const loadClassifications = async () => {
    try {
      setLoading(true);
      const data = await taxClassificationAPI.getAll();
      setClassifications(data);
      setError(null);
    } catch (err) {
      setError(t("admin.errorTaxClassificationLoad"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (classification?: TaxClassification) => {
    if (classification) {
      setEditingId(classification.id || null);
      setFormData({
        displayText: classification.displayText,
        ledgerText: classification.ledgerText,
        taxPercent: classification.taxPercent?.toString() || "",
      });
    } else {
      setEditingId(null);
      setFormData({ displayText: "", ledgerText: "", taxPercent: "" });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({ displayText: "", ledgerText: "", taxPercent: "" });
  };

  const handleSave = async () => {
    if (!formData.displayText.trim() || !formData.ledgerText.trim()) {
      setError(t("admin.taxClassificationRequired"));
      return;
    }

    try {
      const data: TaxClassification = {
        displayText: formData.displayText,
        ledgerText: formData.ledgerText,
        taxPercent: formData.taxPercent
          ? parseFloat(formData.taxPercent)
          : null,
      };

      if (editingId) {
        await taxClassificationAPI.update(editingId, data);
        setSuccess(t("admin.taxClassificationUpdated"));
      } else {
        await taxClassificationAPI.create(data);
        setSuccess(t("admin.taxClassificationCreated"));
      }

      handleCloseDialog();
      await loadClassifications();
    } catch (err) {
      setError(t("admin.errorTaxClassification"));
      console.error(err);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t("admin.taxClassificationDeleteConfirm"))) {
      try {
        await taxClassificationAPI.delete(id);
        setSuccess(t("admin.taxClassificationDeleted"));
        await loadClassifications();
      } catch (err) {
        setError(t("admin.errorTaxClassificationDelete"));
        console.error(err);
      }
    }
  };

  return (
    <Card sx={{ height: "100%", boxShadow: hideHeader ? "none" : undefined }}>
      {!hideHeader && (
        <CardHeader
          title={
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t("admin.taxClassifications") || "Tax Classifications"}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Manage tax classifications and rates
              </Typography>
            </Box>
          }
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              size="small"
            >
              {t("common.add") || "Add"}
            </Button>
          }
        />
      )}
      <CardContent sx={{ p: hideHeader ? 0 : undefined }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            onClose={() => setSuccess(null)}
            sx={{ mb: 2 }}
          >
            {success}
          </Alert>
        )}

        {loading ? (
          <Typography>Loading...</Typography>
        ) : classifications.length === 0 ? (
          <Typography color="textSecondary">
            {t("common.noData") || "No data found"}
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "40px" }}></TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Display Text</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Ledger Text</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Tax %</TableCell>
                  <TableCell sx={{ width: "100px", textAlign: "center" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {classifications.map((classification) => (
                  <TableRow
                    key={classification.id}
                    sx={{
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "#fafafa",
                      },
                    }}
                  >
                    <TableCell sx={{ width: "40px" }}></TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {classification.displayText}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {classification.ledgerText}
                    </TableCell>
                    <TableCell>
                      {classification.taxPercent !== null
                        ? `${classification.taxPercent}%`
                        : "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(classification)}
                        title={t("config.tooltipEdit")}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(classification.id!)}
                        title={t("config.tooltipDelete")}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingId
            ? t("admin.editTaxClassification")
            : t("admin.addTaxClassification")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label={t("admin.displayText")}
            value={formData.displayText}
            onChange={(e) =>
              setFormData({ ...formData, displayText: e.target.value })
            }
            margin="normal"
            placeholder="e.g., Taxable - JCT"
          />
          <TextField
            fullWidth
            label={t("admin.ledgerText")}
            value={formData.ledgerText}
            onChange={(e) =>
              setFormData({ ...formData, ledgerText: e.target.value })
            }
            margin="normal"
            placeholder="e.g., 課対仕入内10%適格"
          />
          <TextField
            fullWidth
            label={t("admin.taxPercent")}
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={formData.taxPercent}
            onChange={(e) =>
              setFormData({ ...formData, taxPercent: e.target.value })
            }
            margin="normal"
            placeholder="e.g., 10 or 7.8 (leave empty for not taxable)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

TaxClassificationCard.displayName = "TaxClassificationCard";

export default React.memo(TaxClassificationCard);
